import { Controller } from "@hotwired/stimulus"

// 회원가입 폼 검증 컨트롤러
// - 제출 버튼은 항상 활성. 클릭 시 비어 있는 필수 항목을 붉은 테두리로 강조.
// - 사용자가 해당 input 에 다시 타이핑하면 강조 자동 해제.
// - 이메일 중복확인 미완료 시에는 기존 inline 안내 + 이메일 input 자체에 붉은 테두리.
export default class extends Controller {
  static targets = ["emailCheckMessage", "submitButton", "nameInput", "loadingSpinner", "buttonText"]
  static outlets = ["email-validation", "password-validation", "password-confirm"]

  connect() {
    this.hideEmailCheckMessage()
    this.isSubmitting = false
  }

  // 이름 입력 이벤트 핸들러 — 기존 호환 유지를 위해 시그니처만 보존.
  onNameInput() {}

  // email-validation 컨트롤러가 상태 변경(입력·중복확인 완료) 시 호출하는 콜백.
  // 이메일이 '사용 가능'으로 확정되면 제출-검증 단계에서 걸었던 '중복확인 미완료' 강조를 해제한다.
  // (없으면 email-validation#notifyFormController 에서 TypeError 발생)
  emailValidationChanged() {
    if (!this.hasEmailValidationOutlet) return
    if (this.emailValidationOutlet.isAvailable) {
      this.clearEmailGroup()
    }
  }

  // password-confirm 컨트롤러가 입력·blur 마다 호출하는 콜백.
  // 이 메서드가 없어서 password_confirm#notifyFormController 가 매 입력마다
  // TypeError 를 던지고 있었다 (email 쪽 emailValidationChanged 만 있고 짝이 빠져 있었다).
  // 일치하면 제출-검증 단계에서 걸었던 강조를 해제한다.
  passwordConfirmChanged() {
    if (!this.hasPasswordConfirmOutlet) return
    if (this.passwordConfirmOutlet.isMatch) {
      this._restoreBorder(this.passwordConfirmOutlet.inputTarget)
    }
  }

  // 폼 제출 전 검증 — 4개 필수 항목 누락 + 이메일 중복확인 미완료 를 한꺼번에 강조.
  // 첫 누락 항목으로 스크롤/포커스. 빈 필드와 [중복확인] 미클릭이 동시에 있어도
  // 둘 다 동시에 빨간 테두리 표시 (한 번에 다 보여줘서 사용자가 한눈에 인지).
  validateSubmit(event) {
    if (this.isSubmitting) {
      event.preventDefault()
      return false
    }

    const checks = []
    if (this.hasEmailValidationOutlet) checks.push(this.emailValidationOutlet.inputTarget)
    if (this.hasPasswordValidationOutlet) checks.push(this.passwordValidationOutlet.inputTarget)
    if (this.hasPasswordConfirmOutlet) checks.push(this.passwordConfirmOutlet.inputTarget)
    if (this.hasNameInputTarget) checks.push(this.nameInputTarget)

    let firstMissing = null
    checks.forEach((el) => {
      if (!el) return
      if (el.value.trim().length === 0) {
        this.markInvalid(el)
        firstMissing ||= el
      }
    })

    // 이메일은 채워졌으나 [중복확인] 미완료 — input + [중복확인] 버튼 둘 다 강조 +
    // "이메일 중복확인을 먼저 진행해주세요" 메시지 노출. 이메일이 비어 있는 케이스는
    // 위 빈 필드 검사에서 이미 input 만 강조 처리됐으므로 여기선 다루지 않음.
    let emailUnverified = false
    if (this.hasEmailValidationOutlet) {
      const emailController = this.emailValidationOutlet
      const emailFilled = emailController.inputTarget.value.trim().length > 0
      if (emailFilled && !emailController.isAvailable) {
        emailUnverified = true
        this.markInvalid(emailController.inputTarget)
        if (emailController.hasCheckButtonTarget) {
          this.markInvalid(emailController.checkButtonTarget)
        }
        firstMissing ||= emailController.inputTarget
      }
    }

    if (emailUnverified) {
      this.showEmailCheckMessage()
    } else {
      this.hideEmailCheckMessage()
    }

    // 비밀번호 확인란은 위 빈 값 검사만 받고 있었다 — 채워져 있기만 하면 값이 달라도
    // 그대로 제출됐다. 화면에는 "일치하지 않습니다" 가 떠 있는데 전송은 되는 상태였다.
    // 서버도 이 값을 검사하지 않아(User 모델에 confirmation 검증이 없었다) 오타가 그대로
    // 비밀번호로 저장됐다. 서버는 별도로 고쳤고, 여기서는 전송 자체를 막는다.
    if (this.hasPasswordConfirmOutlet) {
      // 제출 시점에 다시 계산한다. isMatch 는 확인란의 input·blur 에서만 갱신되는데,
      // 비밀번호 필드의 액션에는 password-confirm#validate 가 없다(두 필드가 형제라
      // Stimulus 액션이 닿지 않는다). 그래서 "확인란을 맞춘 뒤 비밀번호를 고치는" 순서로
      // 입력하면 isMatch 가 낡은 true 로 남는다. validate() 는 메시지 표시도 겸한다.
      this.passwordConfirmOutlet.validate()

      const confirmInput = this.passwordConfirmOutlet.inputTarget
      if (confirmInput.value.trim().length > 0 && !this.passwordConfirmOutlet.isMatch) {
        this.markInvalid(confirmInput)
        firstMissing ||= confirmInput
      }
    }

    if (firstMissing) {
      event.preventDefault()
      firstMissing.scrollIntoView({ behavior: "smooth", block: "center" })
      firstMissing.focus()
      return false
    }

    // 약관 동의는 서버 세션에 있다(Auth::TermsAgreementController#create). 예전에는
    // sessionStorage 에서 읽어 hidden field 로 실어 보냈는데, 그러면 JS 가 실패했을 때
    // 동의가 조용히 사라졌다.

    // 로딩 상태 표시
    this.showLoadingState()

    return true
  }

  // 사용자가 input 에 타이핑하면 해당 항목의 붉은 테두리 강조 해제.
  clearInvalid(event) {
    this._restoreBorder(event.currentTarget)
  }

  // 이메일 영역 (input + [중복확인] 버튼 + 안내 메시지) 일괄 해제.
  // 이메일 input 타이핑 또는 [중복확인] 클릭 시 호출 — 세 요소가 한 그룹.
  clearEmailGroup() {
    if (!this.hasEmailValidationOutlet) return
    this._restoreBorder(this.emailValidationOutlet.inputTarget)
    if (this.emailValidationOutlet.hasCheckButtonTarget) {
      this._restoreBorder(this.emailValidationOutlet.checkButtonTarget)
    }
    this.hideEmailCheckMessage()
  }

  // 누락 항목 강조 — input 은 !important variant 로 border / 바탕색을 덮어
  // focus/hover variant 의 색 변경에도 강조가 유지되게. button 은 인라인 style 의
  // border-color 를 빨강으로 교체 ([중복확인] 버튼은 인라인 style 우선).
  markInvalid(el) {
    if (!el) return
    if (el.tagName === "BUTTON") {
      if (el.dataset.originalBorderColor === undefined) {
        el.dataset.originalBorderColor = el.style.borderColor || ""
      }
      el.style.borderColor = "var(--danger-500)"
    } else {
      el.classList.add("!border-[var(--danger-500)]", "!bg-[var(--danger-50)]")
    }
  }

  // 강조 해제 — markInvalid 의 역연산.
  _restoreBorder(el) {
    if (!el) return
    if (el.tagName === "BUTTON") {
      if (el.dataset.originalBorderColor !== undefined) {
        el.style.borderColor = el.dataset.originalBorderColor
        delete el.dataset.originalBorderColor
      }
    } else {
      el.classList.remove("!border-[var(--danger-500)]", "!bg-[var(--danger-50)]")
    }
  }

  // 로딩 상태 표시
  showLoadingState() {
    this.isSubmitting = true

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }

    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.remove('hidden')
    }

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.classList.add('opacity-0')
    }
  }

  // 로딩 상태 해제 (에러 시 호출)
  hideLoadingState() {
    this.isSubmitting = false

    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.add('hidden')
    }

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.classList.remove('opacity-0')
    }
  }

  showEmailCheckMessage() {
    if (this.hasEmailCheckMessageTarget) {
      this.emailCheckMessageTarget.classList.remove("hidden")
    }
  }

  hideEmailCheckMessage() {
    if (this.hasEmailCheckMessageTarget) {
      this.emailCheckMessageTarget.classList.add("hidden")
    }
  }
}
